import { defineComponent, type App } from 'vue';
import {
  createI18n, Translation, type LocaleMessages, type VueMessageType,
} from 'vue-i18n';
import { loadExternalLanguage, setI18nLanguageExternal } from './external';

function getRegisterMap(path: string[], map: Record<string | symbol, unknown>) {
  const { length } = path;

  if (length > 1) {
    let tMap = map;

    for (let i = 0; i < length - 1; i += 1) {
      const key = path[i];

      if (!tMap[key]) {
        const messages = {};

        tMap[key] = messages;
      }

      tMap = tMap[key] as Record<string | symbol, unknown>;
    }

    return tMap;
  }

  return map;
}

function getFileName(fileName: string) {
  let rst = fileName;

  if (fileName.includes('.')) {
    [rst] = fileName.split('.');
  }

  return rst;
}

/**
 * Load locale messages
 *
 * The loaded `JSON` locale messages is pre-compiled by `@intlify/vue-i18n-loader`,
 * which is integrated into `vue-cli-plugin-i18n`.
 * See: https://github.com/intlify/vue-i18n-loader#rocket-i18n-resource-pre-compilation
 */
export function loadLocaleMessages(
  locales: __WebpackModuleApi.RequireContext,
): LocaleMessages<VueMessageType> {
  const messages: LocaleMessages<VueMessageType> = {};

  locales.keys().forEach((key) => {
    const matched = key.substring(2).split('/');
    let rst = locales(key);

    if (rst.default && typeof rst.default === 'object') {
      rst = rst.default;
    }
    if (matched && matched.length > 1) {
      const { length } = matched;
      const localMap = getRegisterMap(matched, messages);
      const locale = getFileName(matched[length - 1]);

      localMap[locale] = rst;
    } else if (matched?.length === 1) {
      const locale = getFileName(matched[0]);

      messages[locale] = rst;
    }
  });

  return messages;
}

// 自动从 src/locales/ 目录结构中读取支持的语言列表
// 新增语言只需在 src/locales/ 下创建对应目录（如 ja-JP/lang.ts）
const localesContext = require.context('../../locales', true, /\/lang\.ts$/);
const SUPPORTED_LOCALES = localesContext.keys().map((key: string) => key.split('/')[1]);

const RAW_FALLBACK_LOCALE = process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en-US';
const FALLBACK_LOCALE = SUPPORTED_LOCALES.includes(RAW_FALLBACK_LOCALE)
  ? RAW_FALLBACK_LOCALE
  : (SUPPORTED_LOCALES[0] || 'en-US');

const i18nPlugin = createI18n({
  legacy: false,
  globalInjection: true,
  locale: process.env.VUE_APP_I18N_LOCALE || 'en-US',
  fallbackLocale: FALLBACK_LOCALE,
});

export default function i18nInstall(app: App) {
  app.component('I18nT', defineComponent(Translation));
  app.use(i18nPlugin);
}

export const i18n = i18nPlugin.global;

const loadedLanguages: string[] = [];

function resolveLocale(lang: string): string {
  // 精确匹配（如 'zh-CN' -> 'zh-CN'）
  if (SUPPORTED_LOCALES.includes(lang)) {
    return lang;
  }

  // 前缀匹配（如 'zh-TW' -> 'zh-CN'、'en-GB' -> 'en-US'）
  const prefix = lang.split('-')[0].toLowerCase();

  // 如果前缀本身就是受支持的语言（如 'zh'、'en'），直接返回
  if (SUPPORTED_LOCALES.includes(prefix)) {
    return prefix;
  }

  const match = SUPPORTED_LOCALES.find(
    (locale) => locale.split('-')[0].toLowerCase() === prefix,
  );

  return match || FALLBACK_LOCALE;
}

async function setI18nLanguage(lang: string) {
  i18n.locale.value = lang;

  await setI18nLanguageExternal(lang);

  return lang;
}

async function loadLocale(language: string) {
  if (loadedLanguages.includes(language)) {
    return;
  }

  const messages = await import(
    /* webpackChunkName: "lang-[request]" */ `../../locales/${language}/lang.ts`
  );

  i18n.setLocaleMessage(language, messages.default);

  const externalLang = await loadExternalLanguage(language);

  externalLang.forEach((l) => {
    i18n.mergeLocaleMessage(language, l);
  });

  loadedLanguages.push(language);
}

// 不给默认值避免系统初始化（返回''）无法加载浏览器默认语言
export async function loadLanguageAsync(lang: string | undefined) {
  const language = resolveLocale(lang || window.navigator.language);

  // 确保 fallback 语言已加载，否则缺失的 key 无法回退
  const localesToLoad = [...new Set([FALLBACK_LOCALE, language])];

  await Promise.all(localesToLoad.map(loadLocale));

  return setI18nLanguage(language);
}
